import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type CustomerType = 'INDIVIDUAL' | 'CORPORATE';
export type CustomerStatus = 'ACTIVE' | 'SUSPENDED' | 'DELETED';

export interface Customer {
  id: string;
  customerCode: string;
  customerType: CustomerType;
  status: CustomerStatus;
  name: string;
  taxId: string | null;
  phone: string;
  email: string | null;
  lineId: string | null;
  address: string | null;
  fileUrl: string | null;
  salesOwnerId: string;
  createdById: string;
  salesOwner: { firstName: string; lastName: string };
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerRequest {
  customerType: CustomerType;
  name: string;
  taxId: string;
  phone: string;
  email: string;
  lineId: string;
  address: string;
}

interface CreateCustomerResponse {
  message: string;
  customer: Customer;
}

interface CustomersResponse {
  customers: Customer[];
}

export interface CustomerOption {
  id: string;
  name: string;
}

interface CustomerOptionsResponse {
  customerOptions: CustomerOption[];
}

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/customers`;

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }

  async getAll(): Promise<Customer[]> {
    const response = await firstValueFrom(
      this.http.get<CustomersResponse>(this.apiUrl, {
        withCredentials: true,
      }),
    );
    return response.customers;
  }

  async getOptions(status?: CustomerStatus): Promise<CustomerOption[]> {
    const response = await firstValueFrom(
      this.http.get<CustomerOptionsResponse>(`${this.apiUrl}/options`, {
        params: status ? { status } : undefined,
        withCredentials: true,
      }),
    );
    return response.customerOptions;
  }

  getActiveOptions(): Promise<CustomerOption[]> {
    return this.getOptions('ACTIVE');
  }

  async create(input: CreateCustomerRequest, attachment: File | null): Promise<Customer> {
    return this.saveMultipart(this.apiUrl, input, attachment, 'post');
  }

  async update(
    customerId: string,
    input: CreateCustomerRequest,
    attachment: File | null,
  ): Promise<Customer> {
    return this.saveMultipart(`${this.apiUrl}/${customerId}`, input, attachment, 'put');
  }

  async delete(customerId: string): Promise<Customer> {
    const response = await firstValueFrom(
      this.http.delete<CreateCustomerResponse>(`${this.apiUrl}/${customerId}`, {
        withCredentials: true,
      }),
    );
    return response.customer;
  }

  private async saveMultipart(
    url: string,
    input: CreateCustomerRequest,
    attachment: File | null,
    method: 'post' | 'put',
  ): Promise<Customer> {
    const formData = new FormData();
    Object.entries(input).forEach(([key, value]) => formData.append(key, value));
    if (attachment) formData.append('attachment', attachment);

    const response = await firstValueFrom(
      this.http.request<CreateCustomerResponse>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.customer;
  }
}
